import React, { useEffect, useState } from "react";
import _ from "lodash";
import { format } from "date-fns";
import styled from "styled-components";

import { useReclamationContext } from "../../../../providers/reclamation/reclamation.provider";
import { getMeInsurranceService } from "../../../../services/microservice/claim/auth/auth-service";
import { IClientClaim } from "../../../../services/microservice/claim/types/claims";
import PrimaryButton from "../../../buttons/PrimaryButton";
import { useStepContext } from "../stepper.provider";
import {
  ICitiesInsurrance,
  ICoverageDevicePlanUser,
  ICreateInsurrance,
  IDeviceUserInsurrance,
  IDocumentsCoveragesInsurrance,
  IGenderInsurrance,
  IPolicyDeviceUserInsurrance,
} from "../../../../providers/reclamation/types/claims";
import { toFormData } from "../../../../util/toFormData";
import { useCreateClaim } from "../../../../services/microservice/claim/create/use-claim";
import ModalBody from "../../components/ModalBody";
import ModalHeader from "../../components/ModalHeader";
import { useModalContext } from "../../../../providers/modal/modal.provider";
import { useLoginClaim } from "../../../../hooks/useLoginClaim";
import { useForm } from "react-hook-form";
import ModalHeaderDobleAuth from "../../components/ModalHeaderDobleAuth";
import ModalBodyDobleAuth from "../../components/ModalBodyDobleAuth";
import { useGenerateCode } from "../../../../services/microservice/claim/code/use-code";

interface ICreateFormValues {
  client: {
    name: string;
    password: string;
  };
  device: IDeviceUserInsurrance | null;
  plan: IPolicyDeviceUserInsurrance | null;
  coverage: ICoverageDevicePlanUser | null;
  documents: IDocumentsCoveragesInsurrance[];
  gender: IGenderInsurrance | null;
  description: string | null;
  linePrincipal: string | null;
  claimDate: Date | null;
  claimCity: ICitiesInsurrance | null;
  imeiSecondary: string | null;
}

interface ICustomStepProps {
  colorPrimary: string;
  dobleAuth?: boolean;
}

type IFormInput = {
  confirm: boolean;
};

const LabelCheckbox = styled.label`
  > input[type="checkbox"]:checked + i {
    background: "#000000" !important;
  }
`;

const SendStepComponet = ({ colorPrimary, dobleAuth }: ICustomStepProps) => {
  const { globalForm, documents, clear } = useReclamationContext();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>();
  const { setCurrentStep } = useStepContext();
  const [user, setUser] = useState<IClientClaim>();
  const { mutateAsync: createClaim, isLoading } = useCreateClaim();
  const { mutateAsync: generateCodeRequest, isLoading: isLoadingGenerateCode } =
    useGenerateCode();
  const { setShow } = useModalContext();
  const { logout } = useLoginClaim();

  const formatSumbitData = (values: ICreateFormValues): ICreateInsurrance => {
    const documentData = {};

    Object.keys(values.documents).forEach((value) => {
      if (values.documents[value].length > 0) {
        _.set(documentData, String(value), values.documents[value][0]);
      }
    });

    return {
      deviceID: values.device?.id,
      principalIMEI: values.device?.imei_uno,
      planID: values.plan?.plan?.id,
      coverageID: values.coverage?.id,
      message: values.description,
      phoneAccident: values.linePrincipal,
      dateAccident: format(values.claimDate!, "yyyy-MM-dd"),
      genderID: values.gender?.id,
      cityID: values.claimCity?.id,
      secondaryIMEI: values?.imeiSecondary || "",
      ...documentData,
    };
  };

  const clearProcess = () => {
    clear();
    setCurrentStep(1);
    logout();
    setShow((prevState) => ({ ...prevState, visible: false }));
  };

  const openModalInfo = () => {
    setShow((prevState) => ({
      ...prevState,
      visible: true,
      content: (
        <div className="w-full justify-center">
          <h3 className="text-center text-2xl font-bold text-textBlack">
            Estimado cliente
          </h3>
          <p className="mx-10 my-5 text-justify text-xl text-textBlack">
            En caso de que la documentación sea aprobada, la reparación del
            equipo, cuando corresponda, tomará aproximadamente treinta (30) días
            hábiles. Este plazo comenzará a contarse desde la fecha de ingreso
            al centro tecnico autorizado por la marca del celular. Durante este
            período, el centro tecnico llevará a cabo las siguientes
            actividades:
          </p>
          <ul className="ml-20 list-decimal">
            <li className="text-xl">Diagnóstico del equipo</li>
            <li className="text-xl">Solicitud de las piezas necesarias</li>
            <li className="text-xl">Reparación del equipo.</li>
          </ul>
          <p className="mx-10 my-5 text-justify text-xl text-textBlack">
            Enviaremos el equipo a un centro tecnico certificado y autorizado
            por la marca, donde se utilizaran los procesos aprobados por el
            fabricante
          </p>
        </div>
      ),
      header: <></>,
      onAccept: clearProcess,
      titleOnAccept: "Aceptar",
      showBtnAccept: true,
    }));
  };

  const onSubmit = () => {
    const data = formatSumbitData(globalForm as unknown as ICreateFormValues);
    const form = toFormData<ICreateInsurrance>(data);
    createClaim({ data: form })
      .then((res) => {
        setShow((prevState) => ({
          ...prevState,
          visible: true,
          header: <ModalHeader success={true} />,
          content: (
            <ModalBody
              title="Reclamación exitosa"
              message={`Tu reclamación fue creada exitosamente, tu número de radicado es No. ${res.data.claim.id}`}
            />
          ),
          onAccept: () => {
            if (dobleAuth) {
              if (globalForm?.coverage.nombre.includes("Daño Parcial")) {
                openModalInfo();
              } else {
                clearProcess();
              }
            } else {
              clearProcess();
            }
          },
          titleOnAccept: "Aceptar",
          withClass: "flex",
          showBtnAccept: true,
        }));
      })
      .catch((err) => {
        if (err.response.status === 400) {
          setShow((prevState) => ({
            ...prevState,
            visible: true,
            header: <ModalHeader success={false} />,
            content: (
              <ModalBody
                title=" No fue posible crear tu reclamación"
                message={err?.response.data.error.message}
              />
            ),
            onAccept: clearProcess,
            titleOnAccept: "Aceptar",
            withClass: "flex",
            showBtnAccept: true,
          }));
        } else {
          setShow((prevState) => ({
            ...prevState,
            visible: true,
            header: <ModalHeader success={false} />,
            content: (
              <ModalBody
                title=" No fue posible crear tu reclamación"
                message="Acaba de ocurrir un problema, lo sentimos."
              />
            ),
            onAccept: clearProcess,
            titleOnAccept: "Aceptar",
            withClass: "flex",
            showBtnAccept: true,
          }));
        }
      });
  };

  const generateCode = async () => {
    try {
      await generateCodeRequest({
        IMEI: globalForm?.device.imei_uno,
        name: user?.name,
        lastName: user?.second_name,
        email: user?.email,
      });
      setShow((prevState) => ({
        ...prevState,
        visible: true,
        header: <ModalHeaderDobleAuth />,
        content: <ModalBodyDobleAuth submit={onSubmit} email={user?.email} />,
        withClass: "flex",
        showBtnAccept: false,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handlePreviusStep = () => {
    setCurrentStep((prev) => prev - 1);
  };
  useEffect(() => {
    getMeInsurranceService().then((res) => setUser(res.data));
  }, []);

  return (
    <section className="-ml-7 w-80 sm:ml-0 sm:w-full">
      <h2 className="mb-5 text-center text-3xl font-bold">
        5. <span style={{ color: colorPrimary }}>Enviar</span>
      </h2>
      <div>
        <table className="text-gray-700 table w-full">
          <tbody>
            <tr>
              <td className="border-t-0">
                <h6 className="mb-4 font-medium">Datos personales</h6>
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                &nbsp;
              </td>
            </tr>
            <tr>
              <td
                className="border-b-0 border-t-0 pb-3 pt-1"
                style={{ width: "10px" }}
              >
                Nombre(s):
              </td>
              <td
                className="border-b-0 border-t-0 pb-3 pt-1 text-end"
                style={{ width: "10px" }}
              >
                {user?.name}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">Apellido(s):</td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {user?.second_name}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Tipo de documento:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {user?.identification_type.name}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Número de documento:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {user?.identification_number}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">Género:</td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.gender?.name}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">Email:</td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {user?.email}
              </td>
            </tr>
            {globalForm?.device?.dispositivo.category.toLowerCase() ===
            "telefono movil" ? (
              <>
                <tr>
                  <td className="border-b-0 border-t-0 pb-3 pt-1">
                    Línea Principal Asegurada:
                  </td>
                  <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                    {globalForm?.device?.linea_uno}
                  </td>
                </tr>
                {globalForm?.device.linea_dos !== "" ? (
                  <tr>
                    <td className="border-b-0 border-t-0 pb-3 pt-1">
                      Línea Secundaria Asegurada:{" "}
                    </td>
                    <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                      {globalForm?.device.linea_dos}
                    </td>
                  </tr>
                ) : null}
              </>
            ) : null}
            <tr>
              <td className="border-t-0">
                <h6 className="mb-4 mt-4 font-medium">Datos del dispositivo</h6>
              </td>
              <td className="border-t-0">&nbsp;</td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Modelo del dispositivo:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.device?.dispositivo.nombre}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">Fabricante:</td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.device.dispositivo.fabricante}
              </td>
            </tr>
            {globalForm?.device.dispositivo.category.toLowerCase() ===
            "telefono movil" ? (
              <tr>
                <td className="border-b-0 border-t-0 pb-3 pt-1">
                  IMEI Principal Asegurado:
                </td>
                <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                  {globalForm?.device.imei_uno}
                </td>
              </tr>
            ) : (
              <tr>
                <td className="border-b-0 border-t-0 pb-3 pt-1">
                  Serial del dispositivo:
                </td>
                <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                  {globalForm?.device.serial}
                </td>
              </tr>
            )}

            {globalForm?.device?.dispositivo?.category.toLowerCase() ===
              "telefono movil" && globalForm?.imeiSecondary !== undefined ? (
              <tr>
                <td className="border-b-0 border-t-0 pb-3 pt-1">
                  IMEI Secundario Asegurado:
                </td>
                <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                  {globalForm?.imeiSecondary}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className="border-t-0">
                <h6 className="mb-4 mt-4 font-medium">Datos del evento</h6>
              </td>
              <td className="border-t-0">&nbsp;</td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Nombre del plan al que aplica:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.plan.plan.nombre}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Aplicando a la cobertura de:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.coverage.nombre}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Valor de cobertura
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                ${" "}
                {globalForm?.device.policy[0].valor_asegurado
                  .toString()
                  .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Fecha del evento:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.claimDate.toISOString().split("T")[0]}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Hora del evento:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.claimHour?.toLocaleTimeString()}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Ciudad en donde sucedió el evento:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.claimCity.nombre}{" "}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Línea con la que sucedió el evento:
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.linePrincipal}
              </td>
            </tr>
            <tr>
              <td className="border-b-0 border-t-0 pb-3 pt-1">
                Descripción del evento
              </td>
              <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                {globalForm?.description}
              </td>
            </tr>
            <tr>
              <td className="border-t-0">
                <h6 className="mb-4 mt-4 font-medium">Documentos cargados</h6>
              </td>
              <td className="border-t-0">&nbsp;</td>
            </tr>
            {documents.map((doc) =>
              globalForm.documents[doc.id]?.length > 0
                ? globalForm?.documents[doc.id] && (
                    <tr key={doc.id}>
                      <td className="border-b-0 border-t-0 pb-3 pt-1">
                        {doc?.nombre_documento_save}
                      </td>
                      <td className="border-b-0 border-t-0 pb-3 pt-1 text-end">
                        {globalForm?.documents[doc.id][0]?.name}
                      </td>
                    </tr>
                  )
                : null,
            )}
          </tbody>
        </table>
        <LabelCheckbox>
          <input
            {...register("confirm", {
              required: {
                value: true,
                message: "* Requerido",
              },
            })}
            id="confirm"
            name="confirm"
            type="checkbox"
            className="mt-5"
          />
          <i></i> Confirmas tus datos.
        </LabelCheckbox>
        <div className="mb-5 h-1">
          {errors && (
            <p className="my-0 py-0 text-sm text-critical">
              {errors.confirm?.message}
            </p>
          )}
        </div>
        <div className="mt-4 flex justify-between">
          <PrimaryButton
            title="Atras"
            className="rounded-2xl border px-5 text-textBlack"
            onClick={() => handlePreviusStep()}
            type="button"
          />
          {dobleAuth ? (
            <PrimaryButton
              onClick={handleSubmit(generateCode)}
              title="Enviar"
              className="rounded-2xl bg-buttonBlack px-5 text-white"
              loading={isLoading || isLoadingGenerateCode}
              disabled={isLoading || isLoadingGenerateCode}
            />
          ) : (
            <PrimaryButton
              onClick={handleSubmit(onSubmit)}
              title="Enviar"
              className="rounded-2xl bg-buttonBlack px-5 text-white"
              loading={isLoading}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default SendStepComponet;
